import { useCallback, useState, useEffect, useRef } from 'react'
import axios from 'axios'
import io from 'socket.io-client'

import { Typography, LinearProgress, Box } from '@mui/material'

import UploadProgress from './UploadProgress'
import { useKeyToken } from '../../common/hooks'
import { Error, FileSelector, Message } from '../../common/primitives'
import config from '../../config'

const LIFE_CYCLE = {
  LOADED: 'LOADED',
  CHOSEN: 'CHOSEN',
  IN_PROGRESS: 'IN_PROGRESS',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
}

const Uploader = () => {
  const { key, token } = useKeyToken()

  const [lifeCycle, setLifeCycle] = useState(LIFE_CYCLE.LOADED)
  const [errorType, setErrorType] = useState(null) // 'auth', 'network', 'file', 'server', 'timeout', 'unknown'
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadSpeed, setUploadSpeed] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [fileSize, setFileSize] = useState(0)
  // Queues to store last 10 speed readings to "smoothen out" speed calculations
  const speedQueueRef = useRef([]) 
  const s3SpeedQueuesRef = useRef({})
  
  // For tracking S3 upload progress
  const [s3FileProgress, setS3FileProgress] = useState({})
  const [socketId, setSocketId] = useState(null)
  const lifeCycleRef = useRef(lifeCycle)

  // Handlers hoisted above effects to avoid TDZ when used in dependency arrays
  const handleFinish = useCallback(
    () => setLifeCycle(LIFE_CYCLE.COMPLETED),
    [setLifeCycle]
  )

  const calculateAverageUploadSpeed = useCallback((queue, newSpeed) => {
    queue.push(newSpeed)
    if (queue.length > 10) {
      queue.shift()
    }
    return queue.reduce((sum, s) => sum + s, 0) / queue.length
  }, [])

  const handleError = useCallback(
    (error, type = null) => {
      setLifeCycle(LIFE_CYCLE.ERROR)
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed'
      
      // Determine error type if not explicitly provided
      let detectedType = type
      if (!detectedType) {
        const status = error.response?.status
        const message = errorMessage.toLowerCase()
        
        if (status === 401 || status === 403 || message.includes('authentication') || message.includes('credentials') || message.includes('token') || message.includes('unauthorized')) {
          detectedType = 'auth'
        } else if (status === 413 || message.includes('file size') || message.includes('too large')) {
          detectedType = 'file'
        } else if (status >= 500 || message.includes('server error')) {
          detectedType = 'server'
        } else if (error.code === 'ECONNABORTED' || message.includes('timeout')) {
          detectedType = 'timeout'
        } else if (message.includes('network') || message.includes('connection')) {
          detectedType = 'network'
        } else {
          detectedType = 'unknown'
        }
      }
      
      setErrorType(detectedType)
    },
    [setLifeCycle]
  )

  // Setup Socket.io connection
  useEffect(() => {
    // Don't connect if we don't have credentials yet
    if (!token || !key) {
      console.log('Waiting for auth credentials before connecting Socket.IO')
      return
    }

    // Connect with authentication credentials
    // When running on dev, make sure it's uploading directly to backend to avoid proxy issues with large files
    const socketUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin
    
    console.log('Connecting to Socket.IO:', socketUrl)
    
    const socket = io(socketUrl, {
      auth: {
        token: token,
        key: key
      },
      // Use WebSocket-only transport to avoid session caching issues
      transports: ['websocket'],
      // Force new connection instead of reusing old sessions
      forceNew: true,
      // Close connection immediately when page unloads/refreshes
      closeOnBeforeunload: true,
      // Reconnection settings
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    })

    socket.on('connect', () => {
      console.log('Socket.io connected, ID:', socket.id)
      setSocketId(socket.id)
    })

    socket.on('connect_error', (err) => {
      console.error('Socket.io connection error:', err.message)
      const message = err.message?.toLowerCase() || ''
      
      if (message.includes('authentication') || message.includes('credentials') || message.includes('expired')) {
        setErrorType('auth')
      } else {
        setErrorType('network')
      }
      
      setLifeCycle(LIFE_CYCLE.ERROR)
    })

   socket.on('disconnect', (reason) => {
     console.warn('Socket.io disconnected. Reason:', reason)
     const currentLife = lifeCycleRef.current
     if (currentLife === LIFE_CYCLE.IN_PROGRESS || currentLife === LIFE_CYCLE.PROCESSING) {
       setErrorType('network')
     }
   })


    socket.on('s3-upload-progress', (data) => {
      const speed = Number(data.speed) || 0
      const filename = data.filename

      // For calculating rolling average speed of uploads
      if (!s3SpeedQueuesRef.current[filename]) {
        s3SpeedQueuesRef.current[filename] = []
      }

      // Calculate rolling average speed
      const avgSpeed = calculateAverageUploadSpeed(s3SpeedQueuesRef.current[filename], speed)
      const timeRemaining = avgSpeed > 0 ? (data.total - data.loaded) / avgSpeed : null

      setS3FileProgress((prev) => ({
        ...prev,
        [filename]: {
          percent: parseFloat(data.percent),
          speed: avgSpeed,
          timeRemaining: timeRemaining,
          fileIndex: data.fileIndex,
          fileCount: data.fileCount
        }
      }))
    })

    socket.on('s3-upload-complete', (data) => {
      console.log('S3 Upload Complete:', data)
    })

    socket.on('s3-upload-error', (data) => {
      console.error('S3 Upload Error:', data)
      handleError(new Error(data.error))
    })

    // Cleanup on component unmount
    return () => {
      console.log('Disconnecting Socket.IO...')
      socket.removeAllListeners()
      socket.disconnect()
    }
  }, [handleError, token, key, calculateAverageUploadSpeed])

  // keep ref up to date so socket handlers see the latest lifecycle
  useEffect(() => {
    lifeCycleRef.current = lifeCycle
  }, [lifeCycle])


  const handleSubmit = useCallback(
    (files) => {
      const fileArray = files instanceof FileList || Array.isArray(files) ? Array.from(files) : []
      
      if (!fileArray.length) {
        handleError({ message: 'No files selected for upload' }, 'file')
        return
      }

      // Validate filenames do not contain '%' characters
      const filesWithPercent = fileArray.filter(file => file.name.includes('%'))
      if (filesWithPercent.length > 0) {
        const invalidNames = filesWithPercent.map(f => f.name).join(', ')
        handleError({ 
          message: `File names cannot contain '%' characters. Please rename the following file(s) and try again: ${invalidNames}` 
        }, 'file')
        return
      }

      // Validate socket connection before upload
      if (!socketId) {
        setErrorType('network')
        setLifeCycle(LIFE_CYCLE.ERROR)
        return
      }

      const formData = new FormData()
      
      let totalSize = 0
      fileArray.forEach((file) => {
        formData.append('zipFile', file)
        totalSize += file.size
      })
      
      // Validate combined file size is lower that max allowed
      const maxTotalSizeBytes = config.upload.maxFileSizeGB * 1024 * 1024 * 1024 
      if (totalSize > maxTotalSizeBytes) {
        const totalSizeGB = (totalSize / (1024 ** 3)).toFixed(2)
        handleError({ 
          message: `Total file size (${totalSizeGB} GB) exceeds the maximum allowed size of ${config.upload.maxFileSizeGB} GB. Please upload fewer or smaller files.` 
        }, 'file')
        return
      }
      
      setFileSize(totalSize)
      
      // Pre-populate progress bars for all files (0% initially)
      const initialProgress = {}
      fileArray.forEach((file, index) => {
        initialProgress[file.name] = {
          percent: 0,
          speed: 0,
          timeRemaining: null,
          fileIndex: index + 1,
          fileCount: fileArray.length
        }
      })
      setS3FileProgress(initialProgress)

      speedQueueRef.current = []
      s3SpeedQueuesRef.current = {}

      let startTime = Date.now()
      let lastLoaded = 0
      let lastTime = startTime
      let uploadCompleted = false

      let wakeLock = null
      const preventSleep = async () => {
        try {
          if ('wakeLock' in navigator) {
            wakeLock = await navigator.wakeLock.request('screen')
            console.log('Wake lock acquired')
          }
        } catch (err) {
          console.log('Wake lock failed:', err)
        }
      }
      preventSleep()

      // When running on dev, make sure it's uploading directly to backend to avoid proxy issues with large files
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin
      const uploadUrl = `${backendUrl}/api/upload`
      
      axios({
        method: 'POST',
        url: uploadUrl,
        headers: {
          'X-Key': key,
          'X-Token': token,
          'X-Socket-Id': socketId,
        },
        data: formData,
        timeout: config.upload.timeoutMs,
        onUploadProgress: (progressEvent) => {
          const percentComplete = (progressEvent.loaded && progressEvent.total) 
            ? (progressEvent.loaded / progressEvent.total) * 100 
            : 0
          setUploadProgress(percentComplete)

          const currentTime = Date.now()
          const timeDiff = (currentTime - lastTime) / 1000
          
          if (timeDiff > 0) {
            const bytesDiff = progressEvent.loaded - lastLoaded
            const speed = bytesDiff / timeDiff

            const avgSpeed = calculateAverageUploadSpeed(speedQueueRef.current, speed)
            setUploadSpeed(avgSpeed)

            const bytesRemaining = progressEvent.total - progressEvent.loaded
            const secondsRemaining = avgSpeed > 0 ? (bytesRemaining / avgSpeed) : null
            setTimeRemaining(secondsRemaining)

            lastLoaded = progressEvent.loaded
            lastTime = currentTime
          }
          
           if (percentComplete >= 100 && !uploadCompleted) {
             uploadCompleted = true
            setLifeCycle(LIFE_CYCLE.PROCESSING)
          }
        },
      })
        .then((response) => {
          console.log('Upload successful:', response.data)
          handleFinish()
        })
        .catch((error) => {
          console.error('Upload error:', error)
          
          // Check for partial failure details in response
          if (error.response?.data?.results) {
            const { successCount, failureCount, results } = error.response.data
            const failedFiles = results.filter(r => !r.success)
            const failedNames = failedFiles.map(f => f.filename).join(', ')
            
            const errorMsg = failureCount === results.length
              ? `All ${failureCount} file(s) failed to upload: ${failedNames}`
              : `${failureCount} of ${results.length} file(s) failed to upload: ${failedNames}. ${successCount} file(s) uploaded successfully.`
            
            handleError({ message: errorMsg, response: error.response })
          } else {
            handleError(error)
          }
        })
        .finally(() => {
          if (wakeLock) {
            wakeLock.release().then(() => {
              console.log('Wake lock released')
            })
          }
        })

      setLifeCycle(LIFE_CYCLE.IN_PROGRESS)
    },
    [handleError, handleFinish, key, token, socketId, calculateAverageUploadSpeed]
  )

  const handleSubmitForm = useCallback(
    (event) => {
      event.preventDefault()
      const formData = new FormData(event.target)
      const files = formData.getAll('zipFile')
      handleSubmit(files)
    },
    [handleSubmit]
  )

  const handleSelectFile = useCallback(
    (event) => {
      const files = event.target.files
      setLifeCycle(LIFE_CYCLE.CHOSEN)
      handleSubmit(files)
    },
    [setLifeCycle, handleSubmit]
  )

  const formatSpeed = (bytesPerSecond) => {
    if (!bytesPerSecond || !isFinite(bytesPerSecond) || bytesPerSecond <= 0) return 'Calculating...'
    const mbps = bytesPerSecond / (1024 * 1024)
    return `${mbps.toFixed(0)} MB/s`
  }

  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return 'Calculating...'
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m`
    } else {
      return '< 1 min'
    }
  }
  
  const formatFileSize = (bytes) => {
    const gb = bytes / (1024 ** 3)
    if (gb >= 1) {
      return `${gb.toFixed(2)} GB`
    }
    const mb = bytes / (1024 ** 2)
    return `${mb.toFixed(2)} MB`
  }

  return key ? (
    <>
      <Message>Model reference: {key}</Message>
      {(lifeCycle === LIFE_CYCLE.LOADED ||
        lifeCycle === LIFE_CYCLE.CHOSEN) && (
            <>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '10px' }}>
                Maximum file size: {config.upload.maxFileSizeGB} GB
              </Typography>
              <FileSelector
                onSubmitForm={handleSubmitForm}
                onSelectFileChange={handleSelectFile}
              />
            </>
        )}
      {lifeCycle === LIFE_CYCLE.IN_PROGRESS && (
        <>
          <UploadProgress value={uploadProgress} />
          <Typography variant="body2">
            Speed: {formatSpeed(uploadSpeed)}
          </Typography>
          <Typography variant="body2">
            Time remaining: {formatTime(timeRemaining)}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '10px' }}>
            Uploading file... Please do not close this page.
          </Typography>
        </>
      )}
      {lifeCycle === LIFE_CYCLE.PROCESSING && (
        <>
          <Typography variant="h6" style={{ marginBottom: '15px' }}>
            Transferring to cloud storage...
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: '15px' }}>
            Upload to server complete ({formatFileSize(fileSize)})
          </Typography>
          {Object.entries(s3FileProgress).map(([filename, progress]) => (
            <Box key={filename} sx={{ mb: 3 }}>
              <Typography variant="body2" style={{ marginBottom: '5px' }}>
                <strong>{filename}</strong> - {parseFloat(progress.percent) >= 100 ? 'Completed' : `${progress.percent.toFixed(1)}%`}
              </Typography>
              <LinearProgress variant="determinate" value={parseFloat(progress.percent)} />
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '5px' }}>
                {parseFloat(progress.percent) >= 100 
                  ? 'Completed' 
                  : `Speed: ${formatSpeed(progress.speed)} | Time remaining: ${formatTime(progress.timeRemaining)}`
                }
              </Typography>
            </Box>
          ))}
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '15px', display: 'block' }}>
            Please wait... Do not close this page.
          </Typography>
        </>
      )}
      {lifeCycle === LIFE_CYCLE.COMPLETED && (
        <Typography>
          Upload successful! Your file has been archived.
          <br />
          You can close this page or refresh to upload another file.
        </Typography>
      )}
      {lifeCycle === LIFE_CYCLE.ERROR && (
        <>
          {errorType === 'auth' && (
            <Typography variant="body2" style={{ marginTop: '10px' }}>
              <strong>Authentication Issue</strong>
              <div style={{ marginTop: '8px' }}>
                Your upload link may have expired<br />
                <br />
                Please contact Awa for help<br />
                support@awa.kiwi
              </div>
            </Typography>
          )}
          {errorType === 'file' && (
            <Typography variant="body2" style={{ marginTop: '10px' }}>
              <strong>File Issue</strong>
              <div style={{ marginTop: '8px' }}>
                Check that your upload is under the allowed limit ({config.upload.maxFileSizeGB} GB)<br />
                Make sure that file names do not contain the '%' character<br />
                <br />
                Please contact Awa for help if this issue persists<br />
                support@awa.kiwi
              </div>
            </Typography>
          )}
          {errorType === 'timeout' && (
            <Typography variant="body2" style={{ marginTop: '10px' }}>
              <strong>Upload Timeout</strong>
              <div style={{ marginTop: '8px' }}>
                The upload took too long to complete<br />
                <br />
                Please contact Awa for help if this issue persists<br />
                support@awa.kiwi
              </div>
            </Typography>
          )}
          {errorType === 'server' && (
            <Typography variant="body2" style={{ marginTop: '10px' }}>
              <strong>System Error</strong>
              <div style={{ marginTop: '8px' }}>
                An error was encountered processing your upload<br />
                Please try again in a few minutes<br />
                <br />
                Please contact Awa for help if this issue persists<br />
                support@awa.kiwi
              </div>
            </Typography>
          )}
          {errorType === 'network' && (
            <Typography variant="body2" style={{ marginTop: '10px' }}>
              <strong>Connection Issue</strong>
              <div style={{ marginTop: '8px' }}>
                Check your internet connection is stable<br />
                Refresh the page and try again<br />
                <br />
                Please contact Awa for help if this issue persists<br />
                support@awa.kiwi
              </div>
            </Typography>
          )}
          {(errorType === 'unknown' || !errorType) && (
            <Typography variant="body2" style={{ marginTop: '10px' }}>
              <strong>Unknown Error</strong>
              <div style={{ marginTop: '8px' }}>
                “Something went wrong”<br />
                <br />
                Please contact Awa for help if this issue persists<br />
                support@awa.kiwi
              </div>
            </Typography>
          )}
        </>
      )}
    </>
  ) : (
    <Error message="We're expecting an upload URL. Please try again or contact Awa for help." />
  )
}

export default Uploader
